import {
  all,
  delay,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga/macro';
import { sagaIntl } from '@i18n';
import { enqueueSnackbar } from '@common-modules/snackbar';
import * as Actions from '../actions';
import * as Services from '../services';
import {
  DeleteRedactionCustomStatusRequestPayload,
  ReorderRedactionCustomStatusesRequestPayload,
  SaveRedactionCustomStatusRequestPayload,
  SaveRedactionStatusesRequestPayload,
} from '../models';
import {
  selectHasLoadedRedactionStatuses,
  selectHasPersistedRedactionStatusRecords,
  selectRedactionStatusSdoIdsByStatus,
} from '../selectors';
import { getDefaultRedactionStatusSettings } from '@common-modules/mediaDetails/redactionStatuses';

interface Action<P> {
  readonly type: string;
  readonly payload: P;
}

/** Longest an editor open will wait on the status fetch before proceeding. */
export const REDACTION_STATUS_SETTLE_TIMEOUT_MS = 5_000;

/**
 * Waits for an in-flight status fetch to settle (VE-24546).
 *
 * Editors must evaluate lock-edit policy against the real custom-status SDOs:
 * with an empty customStatuses list, isRedactionStatusLockEdits treats a custom
 * status UUID as unknown and returns false, so a deep link into an editor
 * briefly allows edit and save on a status that locks them.
 *
 * Only a cold open actually waits. Once a fetch has *succeeded* this session the
 * store already holds real statuses, so navigating in from the dashboard skips
 * the wait entirely rather than paying a round trip on every editor open; the
 * refetch the caller kicked off still lands, it just isn't blocked on.
 *
 * A previously *failed* fetch does not count as loaded, so the next editor open
 * waits again rather than short-circuiting on stale emptiness for the rest of
 * the session. That costs one round trip only while statuses are broken, and a
 * failure comes back fast — the timeout below is not the common path.
 *
 * Bounded on purpose — a hung or never-settling fetch must not leave the editor
 * unopenable, so on timeout we proceed with whatever the store has. Lock-edit
 * policy stays safe in that window because it fails closed while
 * `hasLoadedStatuses` is false.
 */
export function* waitForRedactionStatusesSettled() {
  const hasLoaded = yield* select(selectHasLoadedRedactionStatuses);
  if (hasLoaded) {
    return;
  }
  yield* race({
    settled: take([
      Actions.FETCH_REDACTION_STATUSES_SUCCESS,
      Actions.FETCH_REDACTION_STATUSES_FAILURE,
    ]),
    timeout: delay(REDACTION_STATUS_SETTLE_TIMEOUT_MS),
  });
}

/** Long-lived watchers for reading statuses / changing built-in settings. */
export function* watchRedactionStatusSettings() {
  yield* all([
    fork(watchFetchRedactionStatuses),
    fork(watchFetchRedactionStatusesSuccess),
    fork(watchFetchRedactionStatusesFailure),
    fork(watchSaveRedactionStatuses),
    fork(watchSaveRedactionStatusesSuccess),
    fork(watchSaveRedactionStatusesFailure),
  ]);
}

/** Statuses admin-page watchers, including custom CRUD and reordering. */
export function* watchRedactionStatusAdministration() {
  yield* all([
    fork(watchRedactionStatusSettings),
    fork(watchSaveRedactionCustomStatus),
    fork(watchSaveRedactionCustomStatusSuccess),
    fork(watchSaveRedactionCustomStatusFailure),
    fork(watchDeleteRedactionCustomStatus),
    fork(watchDeleteRedactionCustomStatusSuccess),
    fork(watchDeleteRedactionCustomStatusFailure),
    fork(watchRestoreRedactionCustomStatus),
    fork(watchRestoreRedactionCustomStatusSuccess),
    fork(watchRestoreRedactionCustomStatusFailure),
    fork(watchPermanentlyDeleteRedactionCustomStatus),
    fork(watchReorderRedactionCustomStatuses),
    fork(watchReorderRedactionCustomStatusesSuccess),
    fork(watchReorderRedactionCustomStatusesFailure),
  ]);
}

export function* watchFetchRedactionStatuses() {
  yield* takeEvery(
    Actions.FETCH_REDACTION_STATUSES,
    function* fetchRedactionStatusesRequest() {
      yield* put(Services.fetchRedactionStatuses());
    }
  );
}

export function* watchFetchRedactionStatusesFailure() {
  yield* takeEvery(Actions.FETCH_REDACTION_STATUSES_FAILURE, function* () {
    const intl = sagaIntl();
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'fetchRedactionStatusesFailure',
          defaultMessage:
            'An error occurred while retrieving redaction statuses.',
        }),
        variant: 'error',
      })
    );
  });
}

export function* watchSaveRedactionStatuses() {
  yield* takeLatest(
    Actions.SAVE_REDACTION_STATUSES_REQUEST,
    function* saveRedactionStatusesRequest(
      action: Action<SaveRedactionStatusesRequestPayload>
    ) {
      const sdoIdsByStatus = yield* select(selectRedactionStatusSdoIdsByStatus);
      yield* put(
        Services.saveRedactionStatuses({
          redactionStatuses: action.payload.redactionStatuses,
          changedStatusFilter: action.payload.changedStatusFilter,
          silent: action.payload.silent,
          sdoIdsByStatus,
        })
      );
    }
  );
}

/**
 * Materializes the built-in status rows the first time an org is seen, so the
 * settings page has SDOs to update instead of a blank slate.
 *
 * This writes product defaults over whatever is stored, so it is deliberately
 * hard to trigger (VE-24546). It only fires when the org has *never* been
 * observed to have status rows — not merely when this one response came back
 * empty. Without that guard a single empty response (a transient backend blip, a
 * cluster repointed at a different schema id, an org-scoping hiccup) silently
 * replaced the org's real `enabled` / `lockEditing` settings with defaults, which
 * is an authorization change with no audit trail and no undo.
 *
 * `hasSeededSinceFork` below stops a failed seed becoming a retry loop, but it
 * is scoped to this watcher's lifetime, NOT the browser session. That lifetime is
 * the lifetime of the route saga that forked it, which since VE-26729 spans every
 * navigation among the routes sharing that saga (seven routes share initMainPage) and
 * ends only when a route names a *different* saga — so the flag
 * re-arms on far fewer navigations than it used to, and on none of the side-menu
 * switches. Either way the real session-wide guard is the store's
 * `hasPersistedStatusRecords`, which survives navigation entirely and blocks a
 * second seed once the first one succeeded. Don't read this flag as a
 * once-per-session guarantee — nor, now, as re-arming on every route change.
 *
 * NOTE: this cannot detect rows written under a *different* schema id — they
 * read back as absent, so a fresh session against a repointed cluster still
 * seeds. That is a deploy-time concern, documented in
 * `docs/schemas/RedactionStatusesSchema.md`; the fix there is to keep publishing forward
 * minor versions of the same registry rather than repointing.
 */
export function* watchFetchRedactionStatusesSuccess() {
  // Per watcher fork, not per browser session — see the note above.
  let hasSeededSinceFork = false;

  yield* takeEvery(
    Actions.FETCH_REDACTION_STATUSES_SUCCESS,
    function* fetchRedactionStatusesSuccess(action: {
      readonly payload?: {
        readonly structuredDataObjects?: {
          readonly records?: unknown[];
        };
      };
    }) {
      const records = action.payload?.structuredDataObjects?.records || [];
      if (records.length > 0) {
        return;
      }
      if (hasSeededSinceFork) {
        return;
      }

      const hadRecords = yield* select(
        selectHasPersistedRedactionStatusRecords
      );
      if (hadRecords) {
        // Known-good settings exist; an empty response here is a fault, not a
        // signal that this org wants product defaults.
        console.warn(
          '[redactionStatuses] Ignoring empty status fetch — this org already had status records, so default seeding was skipped to avoid overwriting its settings.'
        );
        return;
      }

      hasSeededSinceFork = true;
      yield* put(
        Actions.actionSaveRedactionStatuses({
          redactionStatuses: getDefaultRedactionStatusSettings(),
          silent: true,
        })
      );
    }
  );
}

export function* watchSaveRedactionStatusesSuccess() {
  yield* takeEvery(
    Actions.SAVE_REDACTION_STATUSES_SUCCESS,
    function* (action: Action<{ silent?: boolean }>) {
      if (action.payload.silent) {
        return;
      }
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'saveRedactionStatusesSuccess',
            defaultMessage: 'Saved status successfully.',
          }),
          variant: 'success',
        })
      );
    }
  );
}

export function* watchSaveRedactionStatusesFailure() {
  yield* takeEvery(Actions.SAVE_REDACTION_STATUSES_FAILURE, function* () {
    const intl = sagaIntl();
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'saveRedactionStatusesFailure',
          defaultMessage: 'An error occurred while saving redaction statuses.',
        }),
        variant: 'error',
      })
    );
  });
}

// ---------------------------------------------------------------------------
// Custom statuses (VE-24546)
// ---------------------------------------------------------------------------

export function* watchSaveRedactionCustomStatus() {
  yield* takeLatest(
    Actions.SAVE_REDACTION_CUSTOM_STATUS_REQUEST,
    function* (action: Action<SaveRedactionCustomStatusRequestPayload>) {
      yield* put(
        Services.saveRedactionCustomStatus({
          customStatus: action.payload.customStatus,
          closeDrawer: action.payload.closeDrawer,
        })
      );
    }
  );
}

export function* watchSaveRedactionCustomStatusSuccess() {
  yield* takeEvery(
    Actions.SAVE_REDACTION_CUSTOM_STATUS_SUCCESS,
    function* (action: Action<{ statusLabel: string }>) {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage(
            {
              id: 'saveCustomStatusSuccess',
              defaultMessage: 'Saved status "{name}" successfully.',
            },
            { name: action.payload.statusLabel }
          ),
          variant: 'success',
        })
      );
      yield* put(Actions.actionFetchRedactionStatuses());
    }
  );
}

export function* watchSaveRedactionCustomStatusFailure() {
  yield* takeEvery(Actions.SAVE_REDACTION_CUSTOM_STATUS_FAILURE, function* () {
    const intl = sagaIntl();
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'saveCustomStatusFailure',
          defaultMessage: 'An error occurred while saving the status.',
        }),
        variant: 'error',
      })
    );
  });
}

export function* watchDeleteRedactionCustomStatus() {
  yield* takeLatest(
    Actions.DELETE_REDACTION_CUSTOM_STATUS_REQUEST,
    function* (action: Action<DeleteRedactionCustomStatusRequestPayload>) {
      yield* put(
        Services.deleteRedactionCustomStatus({
          sdoId: action.payload.sdoId,
          statusLabel: action.payload.statusLabel,
        })
      );
    }
  );
}

export function* watchDeleteRedactionCustomStatusSuccess() {
  yield* takeEvery(
    Actions.DELETE_REDACTION_CUSTOM_STATUS_SUCCESS,
    function* (action: Action<{ statusLabel: string }>) {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage(
            {
              id: 'deleteCustomStatusSuccess',
              defaultMessage: 'Deleted status "{name}" successfully.',
            },
            { name: action.payload.statusLabel }
          ),
          variant: 'success',
        })
      );
      yield* put(Actions.actionFetchRedactionStatuses());
    }
  );
}

export function* watchDeleteRedactionCustomStatusFailure() {
  yield* takeEvery(
    Actions.DELETE_REDACTION_CUSTOM_STATUS_FAILURE,
    function* () {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'deleteCustomStatusFailure',
            defaultMessage: 'An error occurred while deleting the status.',
          }),
          variant: 'error',
        })
      );
    }
  );
}

export function* watchRestoreRedactionCustomStatus() {
  yield* takeLatest(
    Actions.RESTORE_REDACTION_CUSTOM_STATUS_REQUEST,
    function* (action: Action<DeleteRedactionCustomStatusRequestPayload>) {
      yield* put(
        Services.restoreRedactionCustomStatus({
          sdoId: action.payload.sdoId,
          statusLabel: action.payload.statusLabel,
        })
      );
    }
  );
}

export function* watchRestoreRedactionCustomStatusSuccess() {
  yield* takeEvery(
    Actions.RESTORE_REDACTION_CUSTOM_STATUS_SUCCESS,
    function* (action: Action<{ statusLabel: string }>) {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage(
            {
              id: 'restoreCustomStatusSuccess',
              defaultMessage: 'Restored status "{name}" successfully.',
            },
            { name: action.payload.statusLabel }
          ),
          variant: 'success',
        })
      );
      yield* put(Actions.actionFetchRedactionStatuses());
    }
  );
}

export function* watchRestoreRedactionCustomStatusFailure() {
  yield* takeEvery(
    Actions.RESTORE_REDACTION_CUSTOM_STATUS_FAILURE,
    function* () {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'restoreCustomStatusFailure',
            defaultMessage: 'An error occurred while restoring the status.',
          }),
          variant: 'error',
        })
      );
    }
  );
}

/** Hard delete from the deleted-statuses view; success/failure toasts and the refetch are shared with soft delete via the DELETE actions. */
export function* watchPermanentlyDeleteRedactionCustomStatus() {
  yield* takeLatest(
    Actions.PERMANENTLY_DELETE_REDACTION_CUSTOM_STATUS_REQUEST,
    function* (action: Action<DeleteRedactionCustomStatusRequestPayload>) {
      yield* put(
        Services.permanentlyDeleteRedactionCustomStatus({
          sdoId: action.payload.sdoId,
          statusLabel: action.payload.statusLabel,
        })
      );
    }
  );
}

export function* watchReorderRedactionCustomStatuses() {
  yield* takeLatest(
    Actions.REORDER_REDACTION_CUSTOM_STATUSES_REQUEST,
    function* (action: Action<ReorderRedactionCustomStatusesRequestPayload>) {
      // Built-in rows are upserted by SDO id keyed on statusKey (VE-24546 #5).
      const sdoIdsByStatus = yield* select(selectRedactionStatusSdoIdsByStatus);
      yield* put(
        Services.reorderRedactionStatusPool({
          customStatuses: action.payload.customStatuses,
          builtInSettings: action.payload.builtInSettings,
          previousCustomStatuses: action.payload.previousCustomStatuses,
          previousBuiltInSettings: action.payload.previousBuiltInSettings,
          sdoIdsByStatus,
        })
      );
    }
  );
}

export function* watchReorderRedactionCustomStatusesSuccess() {
  yield* takeEvery(
    Actions.REORDER_REDACTION_CUSTOM_STATUSES_SUCCESS,
    function* () {
      yield* put(Actions.actionFetchRedactionStatuses());
    }
  );
}

export function* watchReorderRedactionCustomStatusesFailure() {
  yield* takeEvery(
    Actions.REORDER_REDACTION_CUSTOM_STATUSES_FAILURE,
    function* () {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'reorderCustomStatusesFailure',
            defaultMessage: 'An error occurred while reordering statuses.',
          }),
          variant: 'error',
        })
      );
      // Reload so the UI reverts to the last persisted order.
      yield* put(Actions.actionFetchRedactionStatuses());
    }
  );
}
